import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "@tanstack/react-router";
import { useProjectPrefetch } from "@/hooks/useDashboardPrefetch";
import { useProjectDetail } from "@/hooks/useProjectDetail";
// LeftSidebar removed -- provided by DashboardLayout parent
import ActivityTab from "./ActivityTab";
import MeetingsTab from "./MeetingsTab";
import TeamTab from "./TeamTab";
import OverviewTab from "./OverviewTab";
import { useProjectMemory } from "@/hooks/useProjectMemory";
import { useProjectMembers } from "@/hooks/useProjectMembers";
import { $api } from "@/lib/api";


interface ProjectDiscoveryPageProps {
  projectId: string;
}

type TabType = "overview" | "activity" | "meetings" | "team";

export default function ProjectDiscoveryPage({ projectId }: ProjectDiscoveryPageProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [editingProperty, setEditingProperty] = useState<string | null>(null);

  // Use $api.useQuery so data reads from TanStack Query cache
  // (populated by route loader and sidebar hover prefetch)
  const { data: queryProject, isFetching: queryLoading, error: queryError } =
    useProjectDetail(projectId);

  // Local override for optimistic updates from child mutations
  const [projectOverride, setProjectOverride] = useState<any>(null);
  const project = projectOverride ?? queryProject ?? null;
  const loading = !project && queryLoading;
  const error = !project && queryError ? "Failed to load project" : null;

  useProjectPrefetch(projectId);

  // Tab badge counts
  const { data: memory } = useProjectMemory(projectId);
  const { data: projectMeetings = [] } = $api.useQuery(
    "get", "/api/v1/meetings/by-project/{project_id}",
    { params: { path: { project_id: projectId } } },
    { staleTime: 5 * 60 * 1000, enabled: !!projectId },
  );
  const { data: members = [] } = useProjectMembers(projectId);

  const activityCount = useMemo(() => {
    const files = memory?.file_activity;
    if (!files || !Array.isArray(files)) return 0;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return files.filter((f) => f.modified_at && new Date(f.modified_at) >= cutoff).length;
  }, [memory?.file_activity]);

  const handleViewChange = (view: string) => {
    if (view === "home") {
      navigate({ to: "/dashboard" });
    } else {
      // For other views, navigate to dashboard with view search param
      navigate({ to: "/dashboard", search: { view } as any });
    }
  };

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: "overview", label: "Overview" },
    { id: "activity", label: "Activity", count: activityCount || undefined },
    { id: "meetings", label: "Meetings", count: projectMeetings.length || undefined },
    { id: "team", label: "Team", count: members.length || undefined },
  ];

  const getStatusStyle = (status?: string): React.CSSProperties => {
    switch (status?.toLowerCase()) {
      case "active":
      case "in-progress":
      case "in_progress":
        return { backgroundColor: "color-mix(in srgb, var(--success) 15%, transparent)", color: "var(--success)", borderColor: "color-mix(in srgb, var(--success) 30%, transparent)" };
      case "on-hold":
      case "on_hold":
        return { backgroundColor: "color-mix(in srgb, var(--warning) 15%, transparent)", color: "var(--warning)", borderColor: "color-mix(in srgb, var(--warning) 30%, transparent)" };
      case "complete":
      case "completed":
        return { backgroundColor: "color-mix(in srgb, var(--accent) 15%, transparent)", color: "var(--accent)", borderColor: "color-mix(in srgb, var(--accent) 30%, transparent)" };
      case "at-risk":
      case "at_risk":
        return { backgroundColor: "color-mix(in srgb, var(--error) 15%, transparent)", color: "var(--error)", borderColor: "color-mix(in srgb, var(--error) 30%, transparent)" };
      default:
        return { backgroundColor: "var(--secondary)", color: "var(--text-secondary)", borderColor: "var(--border)" };
    }
  };

  const getPriorityStyle = (priority?: string): React.CSSProperties => {
    switch (priority?.toLowerCase()) {
      case "high":
        return { backgroundColor: "color-mix(in srgb, var(--error) 15%, transparent)", color: "var(--error)", borderColor: "color-mix(in srgb, var(--error) 30%, transparent)" };
      case "medium":
        return { backgroundColor: "color-mix(in srgb, var(--warning) 15%, transparent)", color: "var(--warning)", borderColor: "color-mix(in srgb, var(--warning) 30%, transparent)" };
      case "low":
        return { backgroundColor: "color-mix(in srgb, var(--accent) 15%, transparent)", color: "var(--accent)", borderColor: "color-mix(in srgb, var(--accent) 30%, transparent)" };
      default:
        return { backgroundColor: "var(--secondary)", color: "var(--text-secondary)", borderColor: "var(--border)" };
    }
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return "Not Started";
    switch (status.toLowerCase()) {
      case "active":
      case "in-progress":
      case "in_progress":
        return "In-Progress";
      case "on-hold":
      case "on_hold":
        return "On Hold";
      case "complete":
      case "completed":
        return "Complete";
      case "at-risk":
      case "at_risk":
        return "At Risk";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getPriorityLabel = (priority?: string) => {
    if (!priority) return "No Priority";
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const currentUserId = user?.id;
  const isProjectOwner = (project?.owner?.id || project?.owner_id) === currentUserId;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: "var(--accent)" }}></div>
          <p style={{ color: "var(--text-secondary)" }}>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="mb-4" style={{ color: "var(--error)" }}>{error || "Project not found"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden relative">
        <div className="h-full rounded-lg overflow-hidden transition-all duration-300 flex flex-col">
          <div className="w-full px-6 py-4 flex-shrink-0">
            {/* Header with Breadcrumb and Properties */}
            <div className="flex items-start justify-between mb-4">
              {/* Left: Breadcrumb and Title */}
              <div className="flex-1">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>{project.name}</h1>
                </div>
                {/* Subtitle - Discovery Summary */}
                {project.discovery_summary_brief && (
                  <p className="text-lg" style={{ color: "var(--text-primary)" }}>
                    {project.discovery_summary_brief}
                  </p>
                )}
              </div>

              {/* Right: Properties Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Priority -- hidden when null */}
                {project.priority && (
                  <button
                    onClick={() => isProjectOwner && setEditingProperty('priority')}
                    disabled={!isProjectOwner}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium hover:shadow-sm transition-shadow ${
                      isProjectOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                    style={getPriorityStyle(project.priority)}
                  >
                    {getPriorityLabel(project.priority)}
                  </button>
                )}

                {/* Status -- always visible */}
                <button
                  onClick={() => isProjectOwner && setEditingProperty('status')}
                  disabled={!isProjectOwner}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium hover:shadow-sm transition-shadow ${
                    isProjectOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                  }`}
                  style={getStatusStyle(project.status)}
                >
                  {getStatusLabel(project.status)}
                </button>

                {/* Owner -- hidden when null */}
                {(project.owner?.display_name || project.owner?.email) && (
                  <button
                    onClick={() => isProjectOwner && setEditingProperty('owner')}
                    disabled={!isProjectOwner}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium hover:shadow-sm transition-shadow ${
                      isProjectOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                    style={{ backgroundColor: "var(--surface-alt)", color: "var(--text-primary)", borderColor: "var(--border)" }}
                  >
                    {project.owner?.display_name || project.owner?.email}
                  </button>
                )}

                {/* Due Date -- hidden when null */}
                {project.due_date && (
                  <button
                    onClick={() => isProjectOwner && setEditingProperty('dates')}
                    disabled={!isProjectOwner}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium hover:shadow-sm transition-shadow ${
                      isProjectOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                    style={{ backgroundColor: "var(--surface-alt)", color: "var(--text-primary)", borderColor: "var(--border)" }}
                  >
                    {formatDate(project.due_date)}
                  </button>
                )}
              </div>
            </div>

            {/* Tabs - Moved up to match Home tab positioning */}
            <div className="mb-4 flex-shrink-0" style={{ marginLeft: '-8px', marginRight: '-8px', paddingLeft: '8px', paddingRight: '8px' }}>
              <div className="flex items-center gap-6 -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center"
                    style={{
                      color: activeTab === tab.id ? "var(--text-primary)" : "var(--text-secondary)",
                      borderColor: activeTab === tab.id ? "var(--text-primary)" : "transparent",
                      fontWeight: activeTab === tab.id ? 700 : 500,
                    }}
                  >
                    {tab.label}
                    {tab.count != null && tab.count > 0 && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full ml-1.5"
                        style={{ backgroundColor: "var(--secondary)", color: "var(--text-secondary)" }}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden flex" style={{ minHeight: 0 }}>
            {/* Main Content Area */}
            <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
              {activeTab === "overview" && (
                <OverviewTab
                  projectId={projectId}
                  project={project}
                />
              )}

              {/* Activity Tab */}
              {activeTab === "activity" && (
                <ActivityTab projectId={projectId} />
              )}

              {/* Meetings Tab */}
              {activeTab === "meetings" && (
                <MeetingsTab projectId={projectId} />
              )}

              {/* Team Tab */}
              {activeTab === "team" && (
                <TeamTab
                  projectId={projectId}
                  project={project}
                  onProjectUpdate={(updatedProject) => setProjectOverride(updatedProject)}
                />
              )}

              {/* Settings tab removed -- theme toggle in sidebar */}
            </div>
          </div>
        </div>
    </div>
  );
}
